import { computed, inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  FormFactory,
  FormMediatorFormState,
  FormMediatorFormStates,
  FormMediatorMapFormStates
} from '@core/data-access/models/form/form-mediator.model';
import { FormName, FormNames } from '@core/data-access/models/form/form.model';
import { BuyerUuid } from '@core/features/buyer-information/models/buyer-information-store.model';
import { BuyerBillingAddressFormFactory } from '@core/features/buyer-information/services/buyer-billing-address-form-factory.service';
import { BuyerInformationFormFactory } from '@core/features/buyer-information/services/buyer-information-form-factory.service';
import { BuyerPrivacyPolicyFormFactory } from '@core/features/buyer-information/services/buyer-privacy-policy-form-factory.service';
import { HolderUuid } from '@products/models/holder.model';
import { TariffUuid } from '@products/models/tariff.model';
import { INPUT_DEBOUNCE_TIME } from '@shared/tokens';
import { forEach, includes, isEmpty, isEqual, isNil, keys, reduce, takeRight } from 'lodash';
import { debounceTime, filter, map, of, share, switchMap, switchScan } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FormMediator {
  readonly #injector = inject(Injector);
  readonly #inputDebounceTime = inject(INPUT_DEBOUNCE_TIME, { optional: true });

  #formFactoryServices = signal({
    [FormName.BuyerInformation]: inject(BuyerInformationFormFactory),
    [FormName.BuyerBillingAddress]: inject(BuyerBillingAddressFormFactory),
    [FormName.BuyerPrivacyPolicy]: inject(BuyerPrivacyPolicyFormFactory)
  });

  state = this.setState();

  resetFormStates(excludeForms: FormNames) {
    forEach(keys(this.#formFactoryServices()), (formName: FormName) => {
      if (includes(excludeForms, formName)) {
        return;
      }

      this.resetFormState(formName);
    });
  }

  resetFormState(formName: FormName) {
    const formState: FormFactory = this.#formFactoryServices()[formName];

    if (isNil(formState)) {
      return;
    }

    formState.resetFormState();
  }

  addFormService<T>(formName: FormName, service: new (...args: any[]) => T) {
    this.#formFactoryServices.update((formFactoryServices) => ({
      ...formFactoryServices,
      ...{ [formName]: this.#injector.get(service) }
    }));
  }

  removeFormService(formName: FormName) {
    this.#formFactoryServices.update((formFactoryServices) => {
      this.resetFormState(formName);

      delete formFactoryServices[formName];

      return formFactoryServices;
    });
  }

  getFormState(formName: FormName) {
    return this.state()[formName] || signal(undefined);
  }

  formValueChanges$(formName: FormName, selectedFormUuid?: BuyerUuid | HolderUuid | TariffUuid) {
    return runInInjectionContext(this.#injector, () =>
      toObservable(this.getFormState(formName)).pipe(
        map((formState: FormMediatorFormState | FormMediatorMapFormStates) =>
          formState instanceof Map && !isNil(selectedFormUuid)
            ? (formState as FormMediatorMapFormStates)?.get(selectedFormUuid)
            : formState
        ),
        filter((formState) => !isNil(formState)),
        filter(
          ({ dynamicFormFields, form: { formGroup, formName }, person }: FormMediatorFormState) =>
            !isEmpty(dynamicFormFields) && !isNil(formGroup) && !isNil(formName) && !isNil(person)
        ),
        switchMap(({ dynamicFormFields, form, person }) =>
          form.formGroup.valueChanges.pipe(
            debounceTime(this.#inputDebounceTime),
            map((valueChanges) => ({
              dynamicFormFields,
              form,
              person,
              valueChanges
            })),
            switchScan(
              (changes, { dynamicFormFields, form, person, valueChanges }) =>
                of(takeRight([...changes, { dynamicFormFields, form, person, valueChanges }], 2)),
              [{ dynamicFormFields, form, person, valueChanges: form.formGroup.value }]
            )
          )
        ),
        filter(([previousChange, currentChange]) => !isEqual(previousChange.valueChanges, currentChange.valueChanges)),
        share()
      )
    );
  }

  private setState() {
    return computed(() =>
      reduce(
        keys(this.#formFactoryServices()),
        (formStates, formName: FormName) => {
          formStates[formName] = (this.#formFactoryServices()[formName] as FormFactory).state;

          return formStates;
        },
        {} as FormMediatorFormStates
      )
    );
  }
}
